import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Info, Users } from 'lucide-react';

const CONCEPTO_INFO = {
  AFP:     { label: 'AFP',     desc: 'Administradoras de Fondos de Pensiones',       color: 'blue'   },
  SFS:     { label: 'SFS',     desc: 'Seguro Familiar de Salud',                      color: 'green'  },
  SRL:     { label: 'SRL',     desc: 'Seguro de Riesgos Laborales',                   color: 'orange' },
  INFOTEP: { label: 'INFOTEP', desc: 'Instituto de Formación Técnico Profesional',     color: 'gray'   }
};

const DEFAULT_RATES = [
  { concepto: 'AFP',     porcentaje_empleado: 2.87, porcentaje_empleador: 7.10, tope_mensual: 464460 },
  { concepto: 'SFS',     porcentaje_empleado: 3.04, porcentaje_empleador: 7.09, tope_mensual: 232230 },
  { concepto: 'SRL',     porcentaje_empleado: 0,    porcentaje_empleador: 1.20, tope_mensual: 92892  },
  { concepto: 'INFOTEP', porcentaje_empleado: 0,    porcentaje_empleador: 1.00, tope_mensual: null   }
];

function buildEditData(tssRates) {
  const map = Object.fromEntries(tssRates.map(r => [r.concepto, r]));
  return DEFAULT_RATES.map(def => {
    const db = map[def.concepto] || {};
    return {
      concepto:             def.concepto,
      porcentaje_empleado:  db.porcentaje_empleado  != null ? parseFloat(db.porcentaje_empleado)  : def.porcentaje_empleado,
      porcentaje_empleador: db.porcentaje_empleador != null ? parseFloat(db.porcentaje_empleador) : def.porcentaje_empleador,
      tope_mensual:         db.tope_mensual         != null ? parseFloat(db.tope_mensual)         : def.tope_mensual,
      // Estos campos se conservan en BD pero no son editables aquí
      descontar_al_empleado: db.descontar_al_empleado ?? (def.porcentaje_empleado > 0),
      incluir_en_cotizable:  db.incluir_en_cotizable  ?? false
    };
  });
}

export default function TasasTSSTab({ tssRates, saveTSSRates, parametros = [], saveParametros }) {
  const [rows, setRows]                         = useState(() => buildEditData(tssRates));
  const [capitaMonto, setCapitaMonto]           = useState(0);
  const [capitaQuincena, setCapitaQuincena]     = useState('AMBAS');
  const [saving, setSaving]                     = useState(false);
  const [saved,  setSaved]                      = useState(false);

  // Derivar el estado global de incluir_en_cotizable desde las filas
  const incluirIncentivos = rows.some(r => r.incluir_en_cotizable);

  useEffect(() => {
    setRows(buildEditData(tssRates));
  }, [tssRates]);

  // Sincronizar capita_monto y capita_quincena_descuento desde parametros
  useEffect(() => {
    const pm = parametros.find(p => p.parametro === 'capita_monto');
    setCapitaMonto(pm ? parseFloat(pm.valor) || 0 : 0);
    const pq = parametros.find(p => p.parametro === 'capita_quincena_descuento');
    setCapitaQuincena(pq ? pq.valor || 'AMBAS' : 'AMBAS');
  }, [parametros]);

  const handleChange = (concepto, field, value) => {
    setRows(prev => prev.map(r =>
      r.concepto === concepto ? { ...r, [field]: value } : r
    ));
    setSaved(false);
  };

  const handleToggleIncentivos = (checked) => {
    setRows(prev => prev.map(r => ({ ...r, incluir_en_cotizable: checked })));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveTSSRates(rows);
      if (saveParametros) {
        const sin = parametros.filter(
          p => p.parametro !== 'capita_monto' && p.parametro !== 'capita_quincena_descuento'
        );
        await saveParametros([
          ...sin,
          { parametro: 'capita_monto',              valor: String(capitaMonto)   },
          { parametro: 'capita_quincena_descuento', valor: capitaQuincena        },
        ]);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Error al guardar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const totalEmpleado  = rows.reduce((s, r) => s + (parseFloat(r.porcentaje_empleado)  || 0), 0);
  const totalEmpleador = rows.reduce((s, r) => s + (parseFloat(r.porcentaje_empleador) || 0), 0);

  return (
    <div className="space-y-5">
      {/* Info principal */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-semibold mb-1">Tasas Oficiales RD 2024-2025</p>
          <p>
            Los topes salariales son <strong>mensuales</strong>. Para nóminas quincenales se dividen
            automáticamente entre 2 al procesar. Los porcentajes se ingresan como
            valores directos (ej: <code className="bg-blue-100 px-1 rounded">2.87</code> = 2.87%).
          </p>
        </div>
      </div>

      {/* Toggle: Incluir Incentivos en Salario Cotizable */}
      <label className="flex items-start gap-4 p-4 cursor-pointer transition-colors">
        <div className="pt-0.5">
          <input
            type="checkbox"
            checked={incluirIncentivos}
            onChange={e => handleToggleIncentivos(e.target.checked)}
            className="w-5 h-5 text-blue-600 rounded cursor-pointer"
          />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-gray-800">Incluir Incentivos en el Salario Cotizable</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Cuando está activo, los incentivos del empleado se suman a la base sobre la que se calculan las contribuciones de TSS.
            Por defecto está desactivado (los incentivos no cotizan según la práctica habitual en RD).
          </p>
        </div>
        <span className={`text-xs font-bold px-2 py-1 rounded-full self-center ${incluirIncentivos ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-600'}`}>
          {incluirIncentivos ? 'Activo' : 'Inactivo'}
        </span>
      </label>

      {/* Tabla de tasas */}
      <div className="overflow-x-auto border border-gray-200 rounded-xl">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left  text-xs font-bold uppercase text-gray-600">Concepto</th>
              <th className="px-4 py-3 text-right text-xs font-bold uppercase text-gray-600">% Empleado</th>
              <th className="px-4 py-3 text-right text-xs font-bold uppercase text-gray-600">% Empleador</th>
              <th className="px-4 py-3 text-right text-xs font-bold uppercase text-gray-600">Tope Mensual (RD$)</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase text-gray-600">Descontar al Empleado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => {
              const info          = CONCEPTO_INFO[r.concepto];
              const tieneEmpleado = parseFloat(r.porcentaje_empleado) > 0;

              return (
                <tr key={r.concepto} className="hover:bg-gray-50">
                  {/* Concepto */}
                  <td className="px-4 py-4">
                    <div className="font-bold text-gray-800">{info.label}</div>
                    <div className="text-xs text-gray-500">{info.desc}</div>
                  </td>

                  {/* % Empleado */}
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={r.porcentaje_empleado}
                        onChange={e => handleChange(r.concepto, 'porcentaje_empleado', parseFloat(e.target.value) || 0)}
                        className="w-20 text-right px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                      <span className="text-gray-500 text-sm">%</span>
                    </div>
                  </td>

                  {/* % Empleador */}
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={r.porcentaje_empleador}
                        onChange={e => handleChange(r.concepto, 'porcentaje_empleador', parseFloat(e.target.value) || 0)}
                        className="w-20 text-right px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                      <span className="text-gray-500 text-sm">%</span>
                    </div>
                  </td>

                  {/* Tope Mensual */}
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={r.tope_mensual ?? ''}
                        onChange={e => handleChange(r.concepto, 'tope_mensual', e.target.value === '' ? null : parseFloat(e.target.value))}
                        placeholder="Sin tope"
                        className="w-36 text-right px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    {r.tope_mensual == null && (
                      <p className="text-xs text-gray-400 text-right mt-0.5">Sin tope</p>
                    )}
                  </td>

                  {/* Descontar al Empleado */}
                  <td className="px-4 py-4 text-center">
                    {tieneEmpleado ? (
                      <label className="inline-flex flex-col items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(r.descontar_al_empleado)}
                          onChange={e => handleChange(r.concepto, 'descontar_al_empleado', e.target.checked)}
                          className="w-5 h-5 text-blue-600 rounded cursor-pointer"
                        />
                        <span className={`text-xs font-semibold ${r.descontar_al_empleado ? 'text-red-600' : 'text-green-600'}`}>
                          {r.descontar_al_empleado ? 'Sí' : 'Empresa asume'}
                        </span>
                      </label>
                    ) : (
                      <span className="text-xs text-gray-400">N/A</span>
                    )}
                  </td>

                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2 border-gray-200">
            <tr>
              <td className="px-4 py-3 font-bold text-gray-700 text-sm text-right">Total</td>
              <td className="px-4 py-3 text-right font-bold text-gray-800">{totalEmpleado.toFixed(2)}%</td>
              <td className="px-4 py-3 text-right font-bold text-gray-800">{totalEmpleador.toFixed(2)}%</td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Cápita por dependiente adicional */}
      <div className="py-2 px-1">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-800">Cápita ARS por Dependiente Adicional</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Monto mensual fijo que se descuenta al empleado por cada familiar registrado como
              Dependiente Adicional en el módulo de Personas Relacionadas.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Monto mensual (RD$)
              </label>
              <div className="relative w-44">
                <span className="absolute left-3 top-2 text-sm text-gray-400">RD$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={capitaMonto}
                  onChange={e => { setCapitaMonto(parseFloat(e.target.value) || 0); setSaved(false); }}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                />
              </div>
              {capitaMonto > 0 && (
                <span className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded-full font-semibold">
                  RD$ {capitaMonto.toLocaleString('es-DO', { minimumFractionDigits: 2 })} / dep. / mes
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Quincena de descuento (default)
              </label>
              <select
                value={capitaQuincena}
                onChange={e => { setCapitaQuincena(e.target.value); setSaved(false); }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              >
                <option value="AMBAS">Ambas quincenas (50% cada una)</option>
                <option value="Q1">Solo Q1 (monto completo en Q1)</option>
                <option value="Q2">Solo Q2 (monto completo en Q2)</option>
              </select>
              <span className="text-xs text-gray-500">
                Aplica a dependientes sin configuración individual
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-semibold"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Guardando...' : saved ? 'Guardado ✓' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}
